import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, catchError, map, Observable, skip, switchMap, tap, throwError } from 'rxjs';
import { color, EChartsOption } from 'echarts';


@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  private domain = environment.domain;

  private departments_sub =
    new BehaviorSubject<{ strategyId: number, departmentId: number, year: string | number }>({ strategyId: 0, departmentId: 0, year: 0 });
  departments$ = this.departments_sub.asObservable().pipe(skip(1), switchMap((value) => {
    return this.getDepartMentPerformance(value.strategyId, value.departmentId, value.year)
  }));

  private sections_sub = new BehaviorSubject<{ strategyId: number, sectionId: number, year: string | number }>({ strategyId: 0, sectionId: 0, year: 0 });
  sections$ = this.sections_sub.asObservable().pipe(skip(1), switchMap((value) => {
    return this.getSectionPerformance(value.strategyId, value.sectionId, value.year)
  }));

  //project action stream
  private projects_sub = new BehaviorSubject<{ strategyId: number, projectId: number, year: string | number }>({ strategyId: 0, projectId: 0, year: 0 });
  projects$ = this.projects_sub.asObservable().pipe(skip(1), switchMap((value) => {
    return this.getProjectPerformance(value.strategyId, value.projectId, value.year)
  }));



  constructor(private http: HttpClient) { }

  private parsePossiblyCorruptedJson(text: string, urlForLog: string): any {
    try {
      return JSON.parse(text);
    } catch (e: any) {
      // Some backend responses have occasionally been observed to contain extra bytes or malformed fragments.
      // Try a minimal repair by extracting the first JSON object boundaries.
      const first = text.indexOf('{');
      const last = text.lastIndexOf('}');
      if (first >= 0 && last > first) {
        const candidate = text.slice(first, last + 1);
        try {
          console.warn('API returned non-JSON-safe payload; attempting repair', {
            url: urlForLog,
            originalLength: text.length,
            candidateLength: candidate.length,
          });
          return JSON.parse(candidate);
        } catch {
          // fall through to detailed logging below
        }
      }

      const message = typeof e?.message === 'string' ? e.message : String(e);
      const match = message.match(/position\s+(\d+)/i);
      const pos = match ? Number.parseInt(match[1], 10) : null;
      const contextRadius = 160;
      const around =
        typeof pos === 'number' && Number.isFinite(pos)
          ? text.slice(Math.max(0, pos - contextRadius), Math.min(text.length, pos + contextRadius))
          : null;
      const aroundCodes =
        around
          ? Array.from(around.slice(0, 80)).map((ch) => ({ ch, code: ch.codePointAt(0) }))
          : null;
      console.error('Failed to parse JSON response', {
        url: urlForLog,
        message,
        position: pos,
        around,
        aroundCodes,
        prefix: text.slice(0, 200),
        suffix: text.slice(Math.max(0, text.length - 200)),
      });
      throw e;
    }
  }

  private normalizeYearParam(year: string | number): string {
    if (typeof year === 'number' && Number.isFinite(year) && year > 1900) {
      return year.toString();
    }

    const trimmed = String(year ?? '').trim().toLowerCase();
    if (trimmed === 'all' || trimmed === '') {
      return 'all';
    }

    const parsed = Number.parseInt(trimmed, 10);
    if (Number.isFinite(parsed) && parsed > 1900) {
      return parsed.toString();
    }

    return 'all';
  }

  getPerformanceOverview(strategyId: number, year: string | number) {
    const normalizedYear = this.normalizeYearParam(year);
    const params = new HttpParams().set('year', normalizedYear);
    const url = this.domain + `strategies/${strategyId}/performance/overview`;
    return this.http.get(url, { params, responseType: 'text' }).pipe(
      map((text) => this.parsePossiblyCorruptedJson(text, url) as any),
      catchError((err) => {
        const httpErr = err as HttpErrorResponse;
        console.error('getPerformanceOverview failed', {
          strategyId,
          year,
          normalizedYear,
          url: httpErr?.url ?? url,
          status: httpErr?.status,
          statusText: httpErr?.statusText,
          errorBody: httpErr?.error
        });
        return throwError(() => err);
      })
    );
  }
  getPerformancOwnerview(strategyId: number, year: string | number = 'all') {
    const params = new HttpParams().set('year', year.toString());
    return this.http.get(this.domain + `strategies/${strategyId}/performance/ownerview`, { params }).pipe(
      catchError((err) => {
        console.error('getPerformancOwnerview failed', { strategyId, year, err });
        return throwError(() => err);
      })
    );
  }
  getDepartMentPerformance(strategyId: number, departMentId: number, year: string | number) {
    const yearParam = new HttpParams().set('year', year.toString());
    return this.http.get(this.domain + `strategies/${strategyId}/performance/departments/${departMentId}`, { params: yearParam })
  }



  getSectionPerformance(strategyId: number, sectionId: number, year: string | number) {
    const yearParam = new HttpParams().set('year', year.toString());
    return this.http.get(this.domain + `strategies/${strategyId}/performance/sections/${sectionId}`, { params: yearParam })
  }

  getProjectPerformance(strategyId: number, projectid: number, year: string | number) {
    // const yearParam = new HttpParams().set('year', year.toString());
    return this.http.get(this.domain + `strategies/${strategyId}/performance/projects/${projectid}`)
  }

  getIndicatorPerformance(strategyId: number, indicatorId: number, year: string | number) {
    const yearParam = new HttpParams().set('year', year.toString());
    return this.http.get(this.domain + `strategies/${strategyId}/performance/indicators/${indicatorId}`, { params: yearParam })
  }









  setDepartmentSub(value: { strategyId: number, departmentId: number, year: string | number }) {
    this.departments_sub.next(value);
  }

  seSectionSub(value: { strategyId: number, sectionId: number, year: string | number }) {
    this.sections_sub.next(value);
  }
  setProjectSub(value: { strategyId: number, projectId: number, year: string | number }) {
    this.projects_sub.next(value);
  }


  getIndicatorsCharts(strategyId: number) {
    return this.http.get(this.domain + `strategies/${strategyId}/performance/indicators`)
  }

  private MapOverallProgressGaugeOptions(progress: number) {
    return {
      title: {
        text: 'تقدم المبادرات الاستراتيجية',
        left: 'center',
        top: '2%', // ✅ مناسب مع الكرت الجديد
        textStyle: {
          fontSize: 12, // ✅ أصغر، مناسب مع حجم الكرت الجدي
          fontFamily: 'Cairo',
          color: '#263238'
        }
      },
      center: ['50%', '50%'], // ✅ أكثر توازن بالنص
      series: [
        {
          type: 'gauge',
          radius: '75%', // ✅ أصغر ليكون مناسب بصريًا داخل الكرت

          progress: {
            show: true,
            width: 6 // ✅ أرفع لتكون أنيقة
          },
          axisLine: {
            lineStyle: {
              width: 6,
              color: [
                [progress / 100, '#A71932'],
                [1, '#e0e0e0']
              ]
            }
          },
          itemStyle: {
            color: '#A71932'
          },
          axisTick: { show: false },
          splitLine: {
            length: 4, // ✅ أصغر قليلاً
            lineStyle: {
              width: 1,
              color: '#999'
            }
          },
          axisLabel: {
            distance: 6, // ✅ مسافة أقل
            color: '#999',
            fontSize: 9 // ✅ خط أصغر
          },
          anchor: {
            show: true,
            showAbove: true,
            size: 8, // ✅ حجم أصغر للنقطة الوسطى
            itemStyle: { borderWidth: 4 }
          },
          title: { show: false },
          detail: {
            valueAnimation: true,
            fontSize: 14, // ✅ الرقم الرئيسي أصغر قليلًا
            offsetCenter: [0, '85%'], // ✅ الرقم يكون تحت القوس بشكل مرتب
            formatter: '{value}%',
            color: '#A71932'
          },
          data: [{ value: progress }]
        }
      ]
    }


  }





  getOverallProgressGaugeOptions(strategyId: number): Observable<any> {
    return this.http.get<{ progress: number }>(`${this.domain}dashboard/overall-progress/${strategyId}`)
      .pipe(
        map(response => this.MapOverallProgressGaugeOptions(response.progress)) // هنا تبني الـ Option
      );
  }

  getStrategicGoalsCoverageGaugeOptions(strategyId: number): Observable<any> {
    return this.http.get<{ coverageRate: number }>(`${this.domain}dashboard/strategic-goals-coverage-rate/${strategyId}`)
      .pipe(
        map(response => this.getGoalCoverageGaugeOptions(response.coverageRate))
      );
  }

  getGoalCoverageGaugeOptions(coverageRate: number) {
    return {
      title: {
        text: 'نسبة تغطية الأهداف الاستراتيجية',
        left: 'center',
        top: '2%', // ✅ أفضل من 0 للفراغ العلوي
        textStyle: {
          fontSize: 12, // ✅ أصغر، مناسب مع حجم الكرت الجدي
          fontFamily: 'Cairo',
          color: '#263238'
        }
      },
      series: [
        {
          type: 'gauge',
          radius: '75%', // ✅ تصغير نصف القطر ليكون متناسق مع الكرت
          center: ['50%', '50%'], // ✅ توسيط أفضل
          progress: {
            show: true,
            width: 6 // ✅ شريط أنحف
          },
          axisLine: {
            lineStyle: {
              width: 6,
              color: [
                [coverageRate / 100, '#A71932'],
                [1, '#e0e0e0']
              ]
            }
          },
          itemStyle: {
            color: '#A71932'
          },
          axisTick: { show: false },
          splitLine: {
            length: 4,
            lineStyle: {
              width: 1,
              color: '#999'
            }
          },
          axisLabel: {
            distance: 6,
            color: '#999',
            fontSize: 9 // ✅ أصغر
          },
          anchor: {
            show: true,
            showAbove: true,
            size: 8, // ✅ حجم أصغر للنقطة الوسطى
            itemStyle: { borderWidth: 4 }
          },
          title: { show: false },
          detail: {
            valueAnimation: true,
            fontSize: 14, // ✅ تصغير الرقم الأوسط
            offsetCenter: [0, '85%'], // ✅ وضع الرقم بمكان أنيق أسفل القوس
            formatter: '{value}%',
            color: '#A71932'
          },
          data: [
            {
              value: coverageRate
            }
          ]
        }
      ]
    }


  }


  getIndicatorClassificationChartOptions(strategyId: number): Observable<EChartsOption> {
    return this.http.get<any>(`${this.domain}dashboard/indicators/classification-summary/${strategyId}`).pipe(
      map(response => {
        return {
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} ({d}%)'
          },
          legend: {
            show: false
          },
          color: ['#A71932', '#f59e0b', '#ff0000'],
          series: [
            {
              name: 'تصنيف المؤشرات',
              type: 'pie',
              radius: ['35%', '55%'], // ✅ Smaller donut size
              avoidLabelOverlap: false,
              label: {
                show: true,
                position: 'center',
                formatter: '{d}%',
                fontSize: 14, // ✅ Smaller center text
                fontWeight: 'bold'
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: 18,
                  fontWeight: 'bold'
                }
              },
              labelLine: {
                show: false
              },
              data: [
                { 
                  value: response.greenIndicators, 
                  name: 'مؤشرات عالية الأداء',
                  itemStyle: { color: '#A71932' }
                },
                { 
                  value: response.yellowIndicators, 
                  name: 'مؤشرات متوسطة الأداء',
                  itemStyle: { color: '#f59e0b' }
                },
                { 
                  value: response.redIndicators, 
                  name: 'مؤشرات منخفضة الأداء',
                  itemStyle: { color: '#ff0000' }
                }
              ]
            }
          ]
        } as EChartsOption;

      })
    );
  }
  getTop5DepartmentsPerformance(strategyId: number): Observable<any[]> {
    return this.http.get<any[]>(this.domain + `dashboard/top5-departments-performance/${strategyId}`);
  }
  getStrategyDashboardSummary(strategyId: number): Observable<any> {
    return this.http.get<any>(this.domain + `dashboard/summaryCards/${strategyId}`);
  }

  getGoalWeightsByStrategicPlanId(strategicPlanId: number): Observable<any> {
    const url = `${this.domain}dashboard/weights/by-strategic-plan/${strategicPlanId}`;
    return this.http.get<{ success: boolean, data: any[] }>(url).pipe(
      // extract just the data array
      map(response => response.data),
      map(goals => this.mapToChartGoalWeightOptions(goals))
    );
  }

  private mapToChartGoalWeightOptions(goals: any[]): EChartsOption {
    // Filter out zero values
    const filteredGoals = goals.filter(goal => goal.weight > 0);
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const item = params[0];
          return `<div style="font-family: Cairo; padding: 8px;">
            <strong>${item.name}</strong><br/>
            الوزن: <strong>${item.value}%</strong>
          </div>`;
        },
        backgroundColor: '#ffffff',
        borderColor: '#C91F3B',
        borderWidth: 1,
        textStyle: {
          fontWeight: 'bold',
          fontSize: 12,
          fontFamily: 'Cairo',
          color: '#333'
        }
      },
      grid: {
        left: '5%',
        right: '5%',
        bottom: '3%',
        top: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}%',
          fontSize: 10,
          fontFamily: 'Cairo',
          color: '#666'
        },
        splitLine: {
          show: true,
          lineStyle: { type: 'dashed', color: '#e5e7eb' }
        },
        axisTick: { show: false },
        axisLine: { show: false }
      },
      yAxis: {
        type: 'category',
        data: filteredGoals.map(goal => goal.name.length > 15 ? goal.name.substring(0, 15) + '...' : goal.name),
        axisLabel: {
          fontSize: 10,
          fontFamily: 'Cairo',
          color: '#374151',
          margin: 10
        },
        axisTick: { show: false },
        axisLine: { show: false }
      },
      series: [
        {
          name: 'الوزن',
          type: 'bar',
          barWidth: '50%',
          showBackground: true,
          backgroundStyle: { color: 'rgba(97, 161, 174, 0.05)' },
          label: {
            show: true,
            position: 'right',
            formatter: '{c}%',
            fontSize: 11,
            fontWeight: 'bold',
            color: '#666',
            fontFamily: 'Cairo'
          },
          itemStyle: {
            color: (params: any) => {
              const values = filteredGoals.map(g => g.weight);
              const maxValue = Math.max(...values);
              const minValue = Math.min(...values);
              const value = params.value;
              
              // الأكبر أخضر، الأصغر أحمر، الباقي برتقالي
              if (value === maxValue) return '#A71932';
              if (value === minValue) return '#ff0000';
              return '#f59e0b';
            },
            borderRadius: [0, 8, 8, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            }
          },
          data: filteredGoals.map(goal => goal.weight),
          animationDuration: 1500,
          animationEasing: 'cubicOut',
          animationDelay: (idx: number) => idx * 100
        }
      ]
    };
  }




  getIndicatorWeightsByStrategicPlanId(strategicPlanId: number): Observable<any> {
    const url = `${this.domain}dashboard/indicators/weights/by-strategic-plan/${strategicPlanId}`;
    return this.http.get<{ success: boolean, data: any[] }>(url).pipe(
      map(response => response.data),
      map(data => this.mapToChartIndicatorWeightOptions(data))
    );
  }

  private mapToChartIndicatorWeightOptions(data: any[]): EChartsOption {
    // Filter out zero values
    const filteredData = data.filter(item => item.value > 0);
    
    // Generate gradient colors for bars
    const colors = ['#C91F3B', '#7fb9c1', '#96c9d0', '#4f8890', '#5ab3c2', '#b8dbe0', '#3f6d74'];
    
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const item = params[0];
          const dataIndex = parseInt(item.name) - 1; // name is "1", "2", etc.
          const indicatorName = filteredData[dataIndex]?.name || '';
          return `<div style="font-family: Cairo; padding: 8px;">
            <strong>${indicatorName}</strong><br/>
            الوزن: <strong>${item.value}%</strong>
          </div>`;
        },
        backgroundColor: '#ffffff',
        borderColor: '#C91F3B',
        borderWidth: 1,
        textStyle: {
          fontWeight: 'bold',
          fontSize: 11,
          fontFamily: 'Cairo',
          color: '#333'
        }
      },
      grid: {
        left: '3%',
        right: '3%',
        bottom: '15%',
        top: '5%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: filteredData.map((item, idx) => `${idx + 1}`),
        axisLabel: {
          fontSize: 10,
          fontFamily: 'Cairo',
          color: '#666',
          rotate: 0
        },
        axisTick: { show: false },
        axisLine: {
          show: true,
          lineStyle: { color: '#e5e7eb' }
        }
      },
      yAxis: {
        type: 'value',
        max: Math.max(...filteredData.map(d => d.value)) + 10,
        axisLabel: {
          formatter: '{value}%',
          fontSize: 10,
          fontFamily: 'Cairo',
          color: '#666'
        },
        splitLine: {
          show: true,
          lineStyle: { type: 'dashed', color: '#e5e7eb' }
        },
        axisTick: { show: false },
        axisLine: { show: false }
      },
      series: [
        {
          name: 'الوزن',
          type: 'bar',
          barWidth: '60%',
          showBackground: false,
          label: {
            show: true,
            position: 'top',
            formatter: '{c}%',
            fontSize: 10,
            fontWeight: 'bold',
            color: '#C91F3B',
            fontFamily: 'Cairo'
          },
          itemStyle: {
            borderRadius: [8, 8, 0, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(97, 161, 174, 0.5)'
            }
          },
          data: filteredData.map((item, index) => ({
            value: item.value,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: colors[index % colors.length] },
                  { offset: 1, color: colors[index % colors.length] + 'cc' }
                ]
              }
            }
          })),
          animationDuration: 1500,
          animationEasing: 'cubicOut',
          animationDelay: (idx: number) => idx * 100
        }
      ]
    };
  }

  getTopStrategicHighlights(strategyId: number): Observable<any> {
    return this.http.get<any>(this.domain + `dashboard/top-strategic-highlights/${strategyId}`);
  }
  
  // NEW DASHBOARD TAB APIS
  getDepartmentPerformance(strategyId: number): Observable<any> {
    return this.http.get<any>(this.domain + `dashboard/department-performance/${strategyId}`);
  }
  
  getStrategicProjectsData(strategyId: number): Observable<any> {
    return this.http.get<any>(this.domain + `dashboard/strategic-projects/${strategyId}`);
  }
  
  getImpactCompetitiveness(strategyId: number): Observable<any> {
    return this.http.get<any>(this.domain + `dashboard/impact-competitiveness/${strategyId}`);
  }
  
  getEnvironmentalIndicators(strategyId: number): Observable<any> {
    return this.http.get<any>(this.domain + `dashboard/environmental-indicators/${strategyId}`);
  }
}
